import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Navigation, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

const slugifyCity = (city) => (city || "").toLowerCase().trim().replace(/\s+/g, "-");

export default function NearestForumBanner() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [locating, setLocating] = useState(false);
    const [locError, setLocError] = useState(null);

    // Check user dismissal
    useEffect(() => {
        if (typeof window === "undefined") return;
        const dismissedAt = localStorage.getItem("np_nearest_dismissed");
        if (dismissedAt) {
            const ts = parseInt(dismissedAt, 10);
            if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) {
                setDismissed(true);
                setLoading(false);
                return;
            }
        }
        // IP-based lookup
        (async () => {
            try {
                const { data } = await api.get("/geo/nearest-forum");
                setData(data);
            } catch { /* noop */ }
            finally { setLoading(false); }
        })();
    }, []);

    const dismiss = () => {
        setDismissed(true);
        try { localStorage.setItem("np_nearest_dismissed", String(Date.now())); } catch {}
    };

    const useBrowserLocation = () => {
        if (!navigator.geolocation) {
            setLocError("Geolocation not supported by your browser.");
            return;
        }
        setLocating(true); setLocError(null);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { data } = await api.get(
                        `/geo/nearest-forum?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`
                    );
                    setData(data);
                } catch (e) {
                    setLocError("Couldn't fetch nearest forum. Please try again.");
                } finally { setLocating(false); }
            },
            () => {
                setLocating(false);
                setLocError("Location permission denied. You can still browse all forums below.");
            },
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
        );
    };

    if (dismissed || loading) return null;

    const forum = data?.forum;
    const detected = data?.detected_city || forum?.city;
    const showBanner = !!forum;
    const showCta = !forum || data?.detected_via === "none" || data?.detected_via === "ip_non_india";

    if (!showBanner && !showCta) return null;

    return (
        <div className="bg-[var(--np-brand)] text-white border-b border-[var(--np-brand-2)]" data-testid="nearest-forum-banner">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
                {showBanner ? (
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-8 w-8 flex items-center justify-center bg-white/10 shrink-0">
                            <MapPin size={14} className="text-[var(--np-accent)]" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-xs text-white/60 uppercase tracking-wider">
                                {data.detected_via === "browser_geolocation" ? "Your Location" : `Detected: ${detected || forum.state}`}
                            </div>
                            <div className="text-white truncate">
                                Your nearest consumer forum:{" "}
                                <Link
                                    to={`/consumer-forums/${slugifyCity(forum.city)}`}
                                    className="font-medium underline underline-offset-4 decoration-[var(--np-accent)]"
                                    data-testid="nearest-forum-link"
                                >
                                    {forum.name}
                                </Link>
                                {forum._distance_km != null && forum._distance_km > 0 && (
                                    <span className="text-white/60"> • ~{Math.round(forum._distance_km)} km</span>
                                )}
                                {forum.phone && <span className="text-white/60 hidden md:inline"> • <a href={`tel:${forum.phone}`} className="hover:text-white inline-flex items-center gap-1"><Phone size={10} />{forum.phone}</a></span>}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 flex items-center justify-center bg-white/10 shrink-0">
                            <Navigation size={14} className="text-[var(--np-accent)]" />
                        </div>
                        <div className="text-white">Find the nearest consumer forum to your location.</div>
                    </div>
                )}

                <div className="flex items-center gap-3 shrink-0">
                    {showCta && (
                        <button
                            onClick={useBrowserLocation}
                            disabled={locating}
                            className="inline-flex items-center gap-1.5 text-xs bg-[var(--np-accent)] text-white px-3 py-2 hover:bg-[var(--np-accent-hover)] transition-colors"
                            data-testid="use-location-btn"
                        >
                            {locating ? <Loader2 size={12} className="animate-spin" /> : <Navigation size={12} />}
                            {locating ? "Locating..." : "Use my location"}
                        </button>
                    )}
                    {showBanner && (
                        <Link
                            to={`/consumer-forums/${slugifyCity(forum.city)}`}
                            className="inline-flex items-center gap-1.5 text-xs bg-[var(--np-accent)] text-white px-3 py-2 hover:bg-[var(--np-accent-hover)] transition-colors"
                            data-testid="view-nearest-forum-btn"
                        >
                            View details →
                        </Link>
                    )}
                    <button onClick={dismiss} aria-label="Dismiss" className="text-white/50 hover:text-white transition-colors" data-testid="dismiss-nearest-banner">
                        <X size={16} />
                    </button>
                </div>
            </div>
            {locError && (
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-2 text-xs text-white/70" data-testid="geo-error">{locError}</div>
            )}
        </div>
    );
}
